import React from 'react';

import {Trans} from '../../libs/shared/core/src/helpers/internationalization/react';
import {
  CleanHtml,
  emptyCleanHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {isHtml} from '../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {ChannelTypesEnum} from '../../libs/shared/core/src/models/api/channelCommonTypes';
import {PaletteColorsEnum} from '../../libs/shared/core/src/models/api/paletteModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../src/models/api/calendarEventVersionModel';
import {CalendarProviderTypesEnum} from '../../src/models/api/calendarModel';
import {
  CalendarVersionAccessRolesEnum,
  CalendarVersionModel,
} from '../../src/models/api/calendarVersionModel';
import {ParticipantResponseStatusesEnum} from '../../src/models/api/participantModel';
import {
  ArbitraryCalendarEventParticipantModel,
  VisualParticipantModel,
} from '../../src/models/api/visualParticipantModel';
import {textToHtml} from '../services/converters/textToHtmlConverter';

export function renderCalendarEventVersionSummary(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  if (calendarVersion.accessRole === CalendarVersionAccessRolesEnum.FREE_BUSY) {
    return <Trans id="calendar-event-viewer-free-busy-event">Busy</Trans>;
  }

  if (
    calendarEventVersion.visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE &&
    !calendarEventVersion.summary &&
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.READER
  ) {
    return <Trans id="calendar-event-viewer-private-event">Private event</Trans>;
  }

  if (!calendarEventVersion.summary) {
    return <Trans id="calendar-event-viewer-untitled-event">(Untitled event)</Trans>;
  }

  return calendarEventVersion.summary;
}

export function renderDescriptorForResponseStatus(responseStatus: string) {
  switch (responseStatus) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return <Trans id="participant-response-status-yes">yes</Trans>;
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return <Trans id="participant-response-status-maybe">maybe</Trans>;
    case ParticipantResponseStatusesEnum.DECLINED:
      return <Trans id="participant-response-status-no">no</Trans>;
    case ParticipantResponseStatusesEnum.PENDING:
      return <Trans id="participant-response-status-awaiting">awaiting</Trans>;
    default:
      return null;
  }
}

export const defaultCalendarEventMaxParticipants = 10;

// Display of participant tokens depends on how many participants there are:
// Less than 10 or participants are expanded: display all
// >= 10: Display 8 and show a see more button.
export function findNumberOfParticipantsToDisplay(
  attendees: ReadonlyArray<VisualParticipantModel> | ReadonlyArray<ArbitraryCalendarEventParticipantModel>,
  shouldShowAllParticipants: boolean | undefined,
  areParticipantsExpanded: boolean,
) {
  if (shouldShowAllParticipants) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (attendees.length < defaultCalendarEventMaxParticipants || areParticipantsExpanded) {
    return attendees.length;
  }

  return 8;
}

export function getColorFromResponseStatus(
  status: ParticipantResponseStatusesEnum,
): PaletteColorsEnum | undefined {
  switch (status) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return PaletteColorsEnum.GREEN;
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return PaletteColorsEnum.ORANGE;
    case ParticipantResponseStatusesEnum.DECLINED:
      return PaletteColorsEnum.RED;
    default:
      return undefined;
  }
}

// TODO: remove after shipping server-side normalization of calendar event descriptions
export function buildCalendarEventDescriptionHtml(
  description: CleanHtml | undefined,
  channelTypeOrProvider: ChannelTypesEnum | CalendarProviderTypesEnum | undefined,
): CleanHtml {
  if (!description) {
    return emptyCleanHtml;
  }

  if (!isHtml(description)) {
    return textToHtml(description);
  }

  if (
    channelTypeOrProvider === ChannelTypesEnum.GMAIL ||
    channelTypeOrProvider === CalendarProviderTypesEnum.GOOGLE
  ) {
    // Google calendar event descriptions contain newline characters where a <br> tag is expected.
    // Therefore, we replace newlines with <br> tags. This is not done for O365 since the O365
    // event description is a complete HTML document with <br> tags where necessary.
    return iSolemnlySwearThisHtmlIsClean(description.replace(/(?:\r\n|\r|\n)/g, '<br>'));
  }

  return description;
}
